import {useContext, useEffect} from 'react';
import {Navigate, Outlet, Route, Routes, useLocation} from 'react-router-dom';
import DashboardPage from './components/pages/dashboard/DashboardPage';
import Navigation from './components/navigation/Navigation';
import Sidebar from './components/shared/layouts/Sidebar';
import MessagesPage from './components/pages/messages/MessagesPage';
import ProfilePage from './components/pages/profile/ProfilePage';
import ResidencePage from './components/pages/house/ResidencePage';
import OrvPage from './components/pages/orv/OrvPage';
import Flex from './components/shared/layouts/Flex';
import MortgagePage from './components/pages/mortgage/MortgagePage';
import SettingsPage from './components/pages/settings/SettingsPage';
import DocumentRequestDetailPage from './components/pages/requests/documents/DocumentRequestDetailPage';
import PersonalDocumentsPage from './components/pages/documents/PersonalDocumentsPage';
import LoginPage from './components/pages/login/LoginPage';
import ResetPassword from './components/pages/login/ResetPassword';
import CreateAccount from './components/pages/login/CreateAccount';
import ForgotPassword from './components/pages/login/ForgotPassword';
import SwitchMfa from './components/pages/login/SwitchMfa';
import Mfa from './components/pages/login/Mfa';
import UserContext from './context/UserContext';
import NotFound from './components/NotFound';
import ComparePage from './components/pages/compare/ComparePage';
import RecoveryCode from './components/pages/login/RecoveryCode';
import EditName from './components/pages/settings/EditName';
import EditEmail from './components/pages/settings/EditEmail';
import DeniedRequests from './components/pages/settings/DeniedRequests';
import EditPassword from './components/pages/settings/EditPassword';
import AddExtraUser from './components/pages/settings/AddExtraUser';
import QuestionnaireDetailPage from './components/pages/requests/questionnaires/QuestionnaireDetailPage';
import ContactPage from './components/pages/contact/ContactPage';
import SignRequestDetailPage from './components/pages/requests/sign-requests/SignRequestDetailPage';
import RequestsPage from './components/pages/requests/RequestsPage';
import ServicesPage from './components/pages/services/ServicesPage';
import SourceDataDetailPage from './components/pages/requests/source-data/SourceDataDetailPage';
import IncomeInsurancePage from './components/pages/incomeInsurance/IncomeInsurancePage';
import FuneralInsurancePage from './components/pages/funeralInsurance/FuneralInsurancePage';
import WhiteLabelContext from './context/WhiteLabelContext';
import ProductsPage from './components/navigation/ProductsPage';
import {sendDataToReactNative} from './utils/communicateWithRN';
import ContactFAB from './components/shared/layouts/ContactFAB';
import CapitalPage from './components/pages/capital/CapitalPage';
import ConsumerCreditPage from './components/pages/consumerCredit/ConsumerCreditPage';
import EditLanguage from './components/pages/settings/EditLanguage';
import styled from '@emotion/styled';
import TwoFactorAuthWizard from './components/pages/settings/TwoFactorAuthWizard';
import GeneralDocumentsPage from './components/pages/documents/GeneralDocumentsPage';
import {useTranslation} from "react-i18next";
import MaxMortgagePage from "./components/pages/tools/maxMortgage/MaxMortgagePage";
import FinlyFormDetailPage from "./components/pages/requests/finly-forms/FinlyFormDetailPage";
import CalendarPage from "./components/pages/calendar/CalendarPage";
import CalendarWizard from "./components/pages/calendar/CalendarWizard";
import RequestInvite from './components/pages/login/RequestInvite';
import SignRequestSigned from "./components/pages/signRequest/SignRequestSigned";
import CalculationsPage from "./components/pages/calculations/CalculationsPage";
import DossierSelectPage from './components/pages/login/DossierSelectPage';
import InsurancesPage from './components/pages/insurance/InsurancesPage';
import SummaryPage from './components/pages/insurance/SummaryPage/SummaryPage';

const PageWithBackground = styled(Flex)(({ theme, showBackgroundImage }) => ({
  backgroundColor: theme?.colors?.background,
  backgroundImage: showBackgroundImage ? theme.backgroundImage : null
}));

const AuthenticadedLayout = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logoutUser } = useContext(UserContext);
  const { canSendConsumerMessages, showBackgroundImage, forceMfa, multipleDossiers } = useContext(
    WhiteLabelContext
  );

  if (!isAuthenticated()) {
    return <Navigate to="/login" />;
  }

  if (multipleDossiers && user && !user?.dossier) {
    return <Navigate to="/dossier-select" />;
  }

  return (
    <>
      <TwoFactorAuthWizard
        isOpen={!user.mfa_enabled && forceMfa}
        onClose={() => {
          if (window.confirm(t('Let op: 2factor authenticatie is verplicht, je wordt uitgelogd zodra je door gaat.'))) {
            logoutUser();
          }
        }}
      />
      <Sidebar
        sideWidth="272px"
        sidebar={<Navigation />}
        page={
          <PageWithBackground
            justifyContent="center"
            px="l"
            py={{ mobile: 'xl', desktop: '4xl' }}
            minHeight="100vh"
            showBackgroundImage={showBackgroundImage}
          >
            <Outlet />
          </PageWithBackground>
        }
      />
      {canSendConsumerMessages && <ContactFAB />}
    </>
  );
};

const App = () => {
  const { pathname } = useLocation();
  const { gtag } = window;

  useEffect(() => {
    sendDataToReactNative(
      JSON.stringify({ type: 'navigationChange', value: pathname })
    );
  }, [pathname]);

  useEffect(() => {
    if (!gtag) return;

    let pagePath = pathname;
    if (pathname.includes('/password/reset/'))
      pagePath = '/password/reset/:token';
    if (pathname.includes('/aanmelden/met-code/'))
      pagePath = '/aanmelden/met-code/:token';
    if (pathname.includes('/compare/')) pagePath = '/compare/:id';
    if (pathname.includes('/document-requests/'))
      pagePath = '/requests/document-requests/:requestId';
    if (pathname.includes('/sign-requests/'))
      pagePath = '/requests/sign-requests/:requestId';
    if (pathname.includes('/questionnaires/'))
      pagePath = '/requests/questionnaires/:requestId';
    if (pathname.includes('/user/settings/add-extra-user/'))
      pagePath = '/user/settings/add-extra-user/:uuid';

    gtag('config', 'G-GTB5HY2BXZ', { page_path: pagePath });
  }, [gtag, pathname]);

  return (
    <Routes>
      <Route path="/not-found" element={<NotFound />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/password/reset/:token" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/switch-mfa" element={<SwitchMfa />} />
      <Route path="/mfa" element={<Mfa />} />
      <Route path="/recovery-code" element={<RecoveryCode />} />
      <Route path="/aanmelden/met-code/:token" element={<CreateAccount />} />
      <Route path="/request-invite" element={<RequestInvite />} />
      <Route path="/sign-request-signed" element={<SignRequestSigned />} />
      <Route path="/dossier-select" element={<DossierSelectPage />} />

      <Route path="/" element={<AuthenticadedLayout />}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="house" element={<ResidencePage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="calculations/overview" element={<CalculationsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="calendar/new" element={<CalendarWizard />} />
        <Route path="calendar/:id" element={<CalendarWizard />} />

        <Route path="compare" element={<ComparePage />} />
        <Route
          path="compare/veiligophalen"
          element={<ComparePage veiligOphalen={true} />}
        />

        <Route path="documents/received" element={<PersonalDocumentsPage/>} />
        <Route path="documents/general" element={<GeneralDocumentsPage />} />

        <Route path="products">
          <Route path="" element={<ProductsPage />} />
          <Route path="mortgage" element={<MortgagePage />} />
          <Route path="income-insurance" element={<IncomeInsurancePage />} />
          <Route path="funeral-insurance" element={<FuneralInsurancePage />} />
          <Route path="orv" element={<OrvPage />} />
          <Route path="service" element={<ServicesPage />} />
          <Route path="capital" element={<CapitalPage />} />
          <Route path="consumer-credit" element={<ConsumerCreditPage />} />
          <Route path="insurances" element={<InsurancesPage />} />
          <Route path="insurances/:type/:id" element={<SummaryPage />} />
        </Route>

        <Route path="requests">
          <Route path="" element={<RequestsPage />} />
          <Route
            path="document-requests/:requestId"
            element={<DocumentRequestDetailPage />}
          />
          <Route
            path="sign-requests/:documentId"
            element={<SignRequestDetailPage />}
          />
          <Route
            path="questionnaires/:requestId"
            element={<QuestionnaireDetailPage />}
          />
          <Route
            path="source-data/:requestId"
            element={<SourceDataDetailPage />}
          />
          <Route
            path="finly-forms/:finlyFormId"
            element={<FinlyFormDetailPage />}
          />
        </Route>

        <Route path="tools">
          <Route path="max-mortgage" element={<MaxMortgagePage />} />
        </Route>

        <Route path="user/settings">
          <Route path="" element={<SettingsPage />} />
          <Route path="edit-name" element={<EditName />} />
          <Route path="edit-email" element={<EditEmail />} />
          <Route path="edit-password" element={<EditPassword />} />
          <Route path="edit-language" element={<EditLanguage />} />
          <Route path="denied-requests" element={<DeniedRequests />} />
          <Route path="add-extra-user" element={<AddExtraUser />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default App;
